import { useCallback, useMemo } from 'react';
import type { GlobalGrid, PlanCell, StructuralObject } from '../../../types/plan';
import type { PlanningSelectionInfo } from '../../../types/planManagement';
import type { EntityLookup } from '../../../utils/planEntities';
import { isBedEntity } from '../../../utils/planEntities';
import { adjacentPlanCoordinate as adjacentCoordinate, planCellKey } from '../../../utils/planCells';
import { isFenceSubtype } from './planningStructuralObjects';
import type { GridSelectionRect } from './types';

/**
 * Клетка под некрывающим объектом (забор, изгородь, постройка, фонарь).
 * Как и на бэкенде, объект без известного вида считается блокирующим.
 */
const isUnderNonCoveringStructure = (
  cell: PlanCell | undefined,
  coveringStructuralObjectIds: Set<string>,
): boolean => Boolean(cell?.structuralObjectId)
  && !coveringStructuralObjectIds.has(cell!.structuralObjectId!);

export const buildSelectedStructuralBoundaryCrossingCellKeys = (
  cellMap: Map<string, PlanCell>,
  selectedCellKeys: Set<string>,
): Set<string> => {
  const crossingCellKeys = new Set<string>();

  selectedCellKeys.forEach((key) => {
    const [rowStr, colStr] = key.split(':');
    const row = Number(rowStr);
    const col = Number(colStr);
    const structuralObjectId = cellMap.get(key)?.structuralObjectId ?? null;

    const adjacentKeys = [
      planCellKey(adjacentCoordinate(row, -1), col),
      planCellKey(adjacentCoordinate(row, 1), col),
      planCellKey(row, adjacentCoordinate(col, -1)),
      planCellKey(row, adjacentCoordinate(col, 1)),
    ];

    adjacentKeys.forEach((adjacentKey) => {
      if (!selectedCellKeys.has(adjacentKey)) return;
      const adjacentStructuralObjectId = cellMap.get(adjacentKey)?.structuralObjectId ?? null;
      if (adjacentStructuralObjectId === structuralObjectId) return;
      crossingCellKeys.add(key);
      crossingCellKeys.add(adjacentKey);
    });
  });

  return crossingCellKeys;
};

interface UsePlanningSelectionParams {
  selectionStart: { row: number; col: number } | null;
  selectionEnd: { row: number; col: number } | null;
  cellMap: Map<string, PlanCell>;
  globalGrid: GlobalGrid;
  entityLookup: EntityLookup;
  bedsWithPlantings: Set<string>;
  structuralObjects?: StructuralObject[];
}

export const usePlanningSelection = ({
  selectionStart,
  selectionEnd,
  cellMap,
  globalGrid,
  entityLookup,
  bedsWithPlantings,
  structuralObjects,
}: UsePlanningSelectionParams) => {
  const coveringStructuralObjectIds = useMemo(() => new Set(
    (structuralObjects ?? [])
      .filter((item) => item.kind === 'covering')
      .map((item) => item.id),
  ), [structuralObjects]);
  const fenceStructuralObjectIds = useMemo(() => new Set(
    (structuralObjects ?? [])
      .filter((item) => isFenceSubtype(item.subtype))
      .map((item) => item.id),
  ), [structuralObjects]);

  const selectionRect = useMemo<GridSelectionRect | null>(() => {
    if (!selectionStart || !selectionEnd) return null;

    const minRow = Math.min(selectionStart.row, selectionEnd.row);
    const maxRow = Math.max(selectionStart.row, selectionEnd.row);
    const minCol = Math.min(selectionStart.col, selectionEnd.col);
    const maxCol = Math.max(selectionStart.col, selectionEnd.col);

    let rows = maxRow - minRow + 1;
    let cols = maxCol - minCol + 1;
    if (minRow < 0 && maxRow > 0) rows -= 1;
    if (minCol < 0 && maxCol > 0) cols -= 1;

    const maxBoundingBox = globalGrid.maxBoundingBox || 32;
    const exceedsBoundary = rows > maxBoundingBox || cols > maxBoundingBox;

    const selectedCellKeys = new Set<string>();
    for (let row = minRow; row <= maxRow; row += 1) {
      if (row === 0) continue;
      for (let col = minCol; col <= maxCol; col += 1) {
        if (col === 0) continue;
        selectedCellKeys.add(planCellKey(row, col));
      }
    }
    const crossingCellKeys = buildSelectedStructuralBoundaryCrossingCellKeys(cellMap, selectedCellKeys);

    const occupiedCells: string[] = [];
    for (let row = minRow; row <= maxRow; row += 1) {
      if (row === 0) continue;
      for (let col = minCol; col <= maxCol; col += 1) {
        if (col === 0) continue;
        const key = planCellKey(row, col);
        const cell = cellMap.get(key);
        if (
          (cell && (cell.kind as string) !== 'empty')
          || isUnderNonCoveringStructure(cell, coveringStructuralObjectIds)
          || crossingCellKeys.has(key)
        ) {
          occupiedCells.push(key);
        }
      }
    }

    return { minRow, maxRow, minCol, maxCol, rows, cols, occupiedCells, exceedsBoundary, maxBoundingBox };
  }, [selectionStart, selectionEnd, cellMap, globalGrid, coveringStructuralObjectIds]);

  const analyzePlanningSelection = useCallback((
    minRow: number,
    maxRow: number,
    minCol: number,
    maxCol: number,
  ): PlanningSelectionInfo => {
    const selectedCellKeys = new Set<string>();
    let emptyCellCount = 0;
    let pathCellCount = 0;
    let obstacleCellCount = 0;
    let structuralObjectCellCount = 0;
    let nonCoveringStructuralObjectCellCount = 0;
    const structuralObjectIds = new Set<string>();
    const bedEntities = new Map<string, { bedId: string; hasPlanting: boolean; cellCount: number; entity: any }>();

    for (let row = minRow; row <= maxRow; row += 1) {
      if (row === 0) continue;
      for (let col = minCol; col <= maxCol; col += 1) {
        if (col === 0) continue;
        const key = planCellKey(row, col);
        const cell = cellMap.get(key);
        const entity = entityLookup.get(key);

        if (!cell || (cell.kind as string) === 'empty') {
          selectedCellKeys.add(key);
          if (!cell?.structuralObjectId) emptyCellCount += 1;
        } else if (entity && isBedEntity(entity)) {
          if (!bedEntities.has(entity.bedId)) {
            bedEntities.set(entity.bedId, {
              bedId: entity.bedId,
              hasPlanting: bedsWithPlantings.has(entity.bedId),
              cellCount: entity.cells.length,
              entity,
            });
          }
        } else if (cell.kind === 'path') {
          selectedCellKeys.add(key);
          pathCellCount += 1;
        } else if (cell.kind === 'obstacle') {
          selectedCellKeys.add(key);
          obstacleCellCount += 1;
        }

        if (cell?.structuralObjectId) {
          structuralObjectCellCount += 1;
          structuralObjectIds.add(cell.structuralObjectId);
          if (isUnderNonCoveringStructure(cell, coveringStructuralObjectIds)) {
            nonCoveringStructuralObjectCellCount += 1;
          }
        }
      }
    }

    bedEntities.forEach(({ entity }) => {
      if (isBedEntity(entity)) {
        entity.cells.forEach(({ row, col }: { row: number; col: number }) => selectedCellKeys.add(planCellKey(row, col)));
      }
    });

    const beds = Array.from(bedEntities.values()).map(({ bedId, hasPlanting, cellCount }) => ({ bedId, hasPlanting, cellCount }));
    const structuralObjectBoundaryCrossingCellCount = buildSelectedStructuralBoundaryCrossingCellKeys(
      cellMap,
      selectedCellKeys,
    ).size;

    return {
      selectedCellKeys,
      rectMinRow: minRow,
      rectMaxRow: maxRow,
      rectMinCol: minCol,
      rectMaxCol: maxCol,
      emptyCellCount,
      pathCellCount,
      obstacleCellCount,
      structuralObjectCellCount,
      nonCoveringStructuralObjectCellCount,
      structuralObjectBoundaryCrossingCellCount,
      structuralObjectIds: Array.from(structuralObjectIds),
      fenceStructuralObjectIds: Array.from(structuralObjectIds).filter((id) => fenceStructuralObjectIds.has(id)),
      beds,
      hasPlantedBeds: beds.some((b) => b.hasPlanting),
      allEmpty: beds.length === 0
        && pathCellCount === 0
        && obstacleCellCount === 0
        && structuralObjectIds.size === 0,
      hasEntities: beds.length > 0
        || pathCellCount > 0
        || obstacleCellCount > 0
        || structuralObjectIds.size > 0,
      totalCells: selectedCellKeys.size,
    };
  }, [cellMap, entityLookup, bedsWithPlantings, coveringStructuralObjectIds, fenceStructuralObjectIds]);

  return { selectionRect, analyzePlanningSelection };
};
